import { useState, useEffect } from "react";
import { api, createPollingSubscription } from "@/lib/apiClient";
import logger from '@/lib/logger';

export interface RealtimeMetrics {
  activeOrders: number;
  inProgressProcesses: number;
  activeOperators: number;
  completionRate: number;
  alerts: Alert[];
}

export interface Alert {
  id: string;
  type: "warning" | "error" | "info";
  message: string;
  timestamp: string;
  workOrderId?: string;
  processId?: string;
}

export const useRealtimeMetrics = (areaFilter: string = "all") => {
  const [metrics, setMetrics] = useState<RealtimeMetrics>({
    activeOrders: 0,
    inProgressProcesses: 0,
    activeOperators: 0,
    completionRate: 0,
    alerts: [],
  });
  const [loading, setLoading] = useState(true);

  const fetchMetrics = async () => {
    try {
      // In progress processes count
      const { data: inProgressData } = await api
        .from("process_records")
        .select("id")
        .in("status", ["in_progress", "paused"]);

      const inProgressProcesses = inProgressData?.length || 0;

      // Active work orders
      const { data: ordersData } = await api
        .from("work_orders")
        .select("id")
        .in("status", ["pending", "in_progress"]);

      const activeOrders = ordersData?.length || 0;

      // Active operators (operators with processes in progress today)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { data: activeOps } = await api
        .from("process_records")
        .select("operator_id")
        .gte("started_at", today.toISOString())
        .in("status", ["in_progress", "paused"]);

      const uniqueOperators = new Set(activeOps?.map((op: any) => op.operator_id) || []);

      // Completion rate (processes completed today vs total started today)
      const { data: completedData } = await api
        .from("process_records")
        .select("id")
        .gte("started_at", today.toISOString())
        .eq("status", "completed");

      const completedToday = completedData?.length || 0;

      const { data: totalData } = await api
        .from("process_records")
        .select("id")
        .gte("started_at", today.toISOString());

      const totalToday = totalData?.length || 0;

      const completionRate = totalToday > 0
        ? Math.round(completedToday / totalToday * 100)
        : 0;

      // Generate alerts
      const alerts: Alert[] = [];

      // Check for paused processes
      const { data: pausedProcesses } = await api
        .from("process_records")
        .select("id, process_id, work_order_id, started_at")
        .eq("status", "paused")
        .limit(5);

      pausedProcesses?.forEach((process: any) => {
        alerts.push({
          id: `paused-${process.id}`,
          type: "warning",
          message: "Proceso pausado requiere atención",
          timestamp: new Date().toISOString(),
          processId: process.id,
          workOrderId: process.work_order_id,
        });
      });

      // Check for delayed orders (pending for more than 24 hours)
      const yesterday = new Date();
      yesterday.setHours(yesterday.getHours() - 24);
      const { data: delayedOrders } = await api
        .from("work_orders")
        .select("id, order_number, created_at")
        .eq("status", "pending")
        .lt("created_at", yesterday.toISOString())
        .limit(5);

      delayedOrders?.forEach((order: any) => {
        alerts.push({
          id: `delayed-${order.id}`,
          type: "error",
          message: `Orden ${order.order_number} pendiente por más de 24h`,
          timestamp: order.created_at,
          workOrderId: order.id,
        });
      });

      setMetrics({
        activeOrders,
        inProgressProcesses,
        activeOperators: uniqueOperators.size,
        completionRate,
        alerts: alerts.sort((a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        ),
      });
      setLoading(false);
    } catch (error) {
      logger.error("Error fetching metrics:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();

    // Set up polling as alternative to realtime subscriptions
    const subscription = createPollingSubscription(fetchMetrics, 10000);
    subscription.start();

    return () => {
      subscription.stop();
    };
  }, [areaFilter]);

  return { metrics, loading, refresh: fetchMetrics };
};
